import { lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
// The hero is part of the page chunk, not lazy-loaded. It used to arrive as a second chunk
// behind a shorter fallback, and the swap pushed the whole page down (see HeroSection.tsx).
// index.html paints the same hero before any of this runs.
import HeroSection from "@/components/HeroSection";
import HomePrices from "@/components/home/HomePrices";
import HomeGoals from "@/components/home/HomeGoals";
import HomeTrust from "@/components/home/HomeTrust";
import HomeStore from "@/components/home/HomeStore";
import HomeLatest from "@/components/home/HomeLatest";
import { useTrackPageView } from "@/hooks/useTrackPageView";
import SEOHead from "@/components/SEOHead";
import BloodworkDashboardWidget from "@/components/BloodworkDashboardWidget";

const EmailCapture = lazy(() => import("@/components/EmailCapture"));
const Footer = lazy(() => import("@/components/Footer"));

/**
 * Homepage, rebuilt 2026-10-22 on the design system and restyled 2026-10-24 in direction B, "Bright and bold", the
 * owner's pick (docs/design/MASTER.md; what it shows and why is in docs/design/pages/home.md). The hero, then five
 * sections, in this order:
 *
 *   Hero    headline with the rotating word, search, popular compounds, store and compare buttons, the BHG20 line,
 *           from 1024px the helix with two live numbers, cheat sheet signup
 *   Prices  the most viewed compounds at their lowest price per mg, vendor codes
 *   Method  How this site works (the owner's wording), right under the prices it explains
 *   Start   six goal tiles, the other goals, then the tools, the beginner pages and the blood panel referral
 *   Store   Featured store: four BHG Labs products, the code, the store link
 *   Latest  blog posts, recent news, the communities, and "Browse more" (the old desktop link table)
 *
 * Every link the September page carried is still on the page (home.md lists where each one went).
 */
const Index = () => {
  useTrackPageView();
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="BodyHackGuide: Peptide Prices, Dosing & Nootropic Stacks"
        description="Compare research peptide prices across every vendor we track. Free reconstitution calculators, dosage tools, nootropic stacks and AI bloodwork analysis."
        canonical="https://www.bodyhackguide.co"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "BodyHackGuide",
            url: "https://www.bodyhackguide.co",
            description:
              "Research compound price comparison and education platform. Every vendor is ranked on the same public, reproducible methodology, and we earn a commission on purchases through some of our links.",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://www.bodyhackguide.co/compare?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "BodyHackGuide",
            url: "https://www.bodyhackguide.co",
            logo: "https://www.bodyhackguide.co/og-image.png",
            description:
              "Research compound price comparison and education platform. Every vendor is ranked on the same public, reproducible methodology, and we earn a commission on purchases through some of our links.",
            sameAs: [
              "https://reddit.com/r/BodyHackGuide",
              "https://reddit.com/r/BrainHackGuide",
              "https://reddit.com/r/Biohackingher",
              "https://discord.gg/Mhq5UdRYBA",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "BodyHackGuide",
            description: "Research compound price comparison engine with calculators and vendor directory",
            url: "https://www.bodyhackguide.co",
            applicationCategory: "HealthApplication",
            operatingSystem: "All",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          },
        ]}
      />
      <Navbar />
      <HeroSection />
      <HomePrices />
      <HomeTrust />
      <HomeGoals />
      {/* Personal bloodwork card. Renders nothing unless the visitor is signed in and has
          uploaded results, so it costs anonymous visitors nothing. */}
      <BloodworkDashboardWidget />
      <HomeStore />
      <HomeLatest />
      <Suspense fallback={null}>
        {/* Timed and exit-intent email popups only (the owner's call). The inline signup is the cheat sheet form
            in the hero; the page has no second form. */}
        <EmailCapture popupsOnly />
      </Suspense>
      <Suspense fallback={<div className="min-h-[420px]" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
